import React, { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useGetOrderQuery } from '../../apis/mlkApi/mlk.api.hooks';
import { BaseLayout } from '../../components/Layout/Layout';
import { useIsNoAssign } from '../../hooks/useIsNoAssign';
import { useLanguage } from '../../locale/useLanguage';
import { FaqFeature } from '../../models';
import { Routes } from '../../routes/types';
import { HelpCard } from './components/HelpCard/HelpCard';

import './HelpView.css';

type HelpViewRouteParams = {
  mlkId?: string;
};

type HelpViewProps = {};

export const HelpView: React.FC<HelpViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const isNoAssign = useIsNoAssign();
  const { mlkId } = useParams<HelpViewRouteParams>();

  const { data: order } = useGetOrderQuery(
    { orderId: mlkId || '', isNoAssign },
    { skip: !mlkId }
  );

  const showContactPhone =
    !!order?.shipperTerritoryConfiguration.uiContactParams.contactPhone;

  const showContactSite =
    !!order?.shipperTerritoryConfiguration.uiContactParams.contactSite;

  const showContactEmail =
    !!order?.shipperTerritoryConfiguration.uiContactParams.contactEmail;

  useEffect(() => {
    if (!mlkId) {
      history.replace(Routes.HelpSearchOrder);
    }
  }, [history, mlkId]);

  const goToSearchOrder = useCallback(() => {
    history.push(Routes.HelpSearchOrder);
  }, [history]);

  const goToFaqs = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.Order));
  }, [history]);

  const goToHome = useCallback(() => {
    history.push(Routes.Home);
  }, [history]);

  const openContactSite = useCallback(() => {
    if (showContactSite) {
      window.open(
        order.shipperTerritoryConfiguration.uiContactParams.contactSite,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [
    order?.shipperTerritoryConfiguration.uiContactParams.contactSite,
    showContactSite,
  ]);

  const openContactPhone = useCallback(() => {
    if (showContactPhone) {
      window.open(
        `tel:${order.shipperTerritoryConfiguration.uiContactParams.contactPhone}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [
    order?.shipperTerritoryConfiguration.uiContactParams.contactPhone,
    showContactPhone,
  ]);

  const openContactEmail = useCallback(() => {
    if (showContactPhone) {
      window.open(
        `mailto:${order.shipperTerritoryConfiguration.uiContactParams.contactEmail}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [
    order?.shipperTerritoryConfiguration.uiContactParams.contactEmail,
    showContactPhone,
  ]);

  return (
    <BaseLayout
      onCloseClick={goToHome}
      hasClose
      isGreyBg
      title={t('pageTitles.help')}
    >
      {order && (
        <div className="help--body">
          <h2 className="subtitle">{t('help.title')}</h2>
          <p className="help--description">
            {t('help.trackingCode')}: {order.shipperOrderCode}
          </p>
          <p className="help--description">
            {t('help.shipper')}: {order.shipper.alias || order.shipper.name}
          </p>
          <div className="help--link-container">
            <a onClick={goToSearchOrder} role="link" className="link clickable">
              {t('help.changeOrder')}
            </a>
          </div>
          <div className="help--card-first-container">
            <HelpCard
              title={t('help.faq')}
              description={t('help.faqDescription')}
              onClick={goToFaqs}
            />
          </div>
          {showContactEmail && (
            <div className="help--card-container">
              <HelpCard
                title={t('help.writeUs')}
                description={t('help.writeUsDescription', {
                  email:
                    order.shipperTerritoryConfiguration.uiContactParams
                      .contactEmail,
                })}
                onClick={openContactEmail}
              />
            </div>
          )}

          {showContactPhone && (
            <div className="help--card-container">
              <HelpCard
                title={t('help.callUs')}
                description={t('help.callUsDescription', {
                  phoneNumber:
                    order.shipperTerritoryConfiguration.uiContactParams
                      .contactPhone,
                })}
                onClick={openContactPhone}
              />
            </div>
          )}
          {showContactSite && (
            <div className="help--card-container">
              <HelpCard
                title={t('help.website')}
                description={t('help.websiteDescription', {
                  websiteUrl:
                    order.shipperTerritoryConfiguration.uiContactParams
                      .contactSite,
                })}
                onClick={openContactSite}
              />
            </div>
          )}
        </div>
      )}
    </BaseLayout>
  );
};
